import dayjs from 'dayjs';
import Logger from 'services/Logger/Logger';
import { termsAndConditionsSelector } from 'store/global/selectors';
import { handleLoginPress } from 'store/pageActions/handleLoginPress';
import { RootDispatch, RootState } from 'store/types';
import {
  submitRegistration,
  updateRegistrationAlert,
  updateRegistrationData,
  updateRegistrationStep,
  getRedirectUrl,
  resetRegistration,
  RegisterData,
  AlertType,
  UnifiedRedirectActions,
  UnifiedRedirectKey,
  getRegisterBrand,
  getRedirectIdx
} from 'store/register';
import { History } from 'lib/history';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { forgotUsername } from 'store/authentication/actions';
import { Step, errorStatusCodes, formatAlert } from './constants';

/**
 * Method: handleError
 * Description: Handle Registration errors. Gives an alert for known errors. Navigates to the error page if unknown error
 */
export const handleError = (
  dispatch: RootDispatch,
  statusCode: string,
  statusDescription: string
) => {
  if (
    statusCode === errorStatusCodes.DUPLICATE_USERNAME ||
    statusCode === errorStatusCodes.DUPLICATE_ACCOUNT
  ) {
    dispatch(updateRegistrationAlert(formatAlert(AlertType.ERROR, 'Uh oh!', statusDescription)));
  } else {
    dispatch(updateRegistrationStep(Step.ERROR));
  }
};

/**
 * Method: attemptRegistration
 * Description: Attempts to register user. If successful it send them to the Success page. On Error it sends them to the error page.
 */
export const attemptRegistration = (dispatch: RootDispatch, retry = 0) => {
  return new Promise((resolve, reject) => {
    dispatch(submitRegistration()).then(res => {
      if (!res.error) {
        resolve(dispatch(updateRegistrationStep(Step.SUCCESS)));
      } else {
        const { status, data } = res.error?.response || {};
        const statusCode = data?.statusCode || data?.data?.statusCode || '';
        const statusDescription = data?.statusDescription || data?.data?.statusDescription || '';

        if (status >= 500 || (statusCode && statusDescription)) {
          handleError(dispatch, statusCode, statusDescription);
          reject(res.error);
        } else if (res.error && retry < 2) {
          attemptRegistration(dispatch, retry + 1).then(resolve, reject);
        } else {
          dispatch(updateRegistrationStep(Step.ERROR));
          reject(res.error);
        }
      }
    }, reject);
  });
};

/**
 * Method: handleSubmit
 * Description: Prepares data and state for attemptRegistration
 */
export const handleSubmit = (values: Partial<RegisterData>) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const state = getState();
  dispatch(updateRegistrationAlert(formatAlert()));

  const terms = termsAndConditionsSelector(state);
  const termsVersion = terms?.general?.version || '';
  const { username, password } = values;

  dispatch(
    updateRegistrationData({
      username,
      password,
      termsAcceptance: {
        type: 'GENERAL',
        version: termsVersion,
        date: dayjs().toISOString()
      }
    })
  );

  attemptRegistration(dispatch).catch(err => Logger.warn('registeration failed ', err));
};

/**
 * Method: logExitFlow
 * Description: Sends off AnalyticsEvent when the flow is exited prematurely.
 */
export function logExitFlow(eventData: AmplitudeEventData) {
  analyticsService.logEvent(AnalyticsEvent.AccountCreationFlowExited, eventData);
}

/**
 * Method: handleReturnToInitiator
 * Description: determines the appropriate navigation path given the state and redirectIdx
 */
export async function handleReturnToInitiator(
  status: UnifiedRedirectKey,
  history: History,
  dispatch: RootDispatch
) {
  // Get the redirectIdx
  const redirectIdx = getRedirectIdx();
  const redirectIdxTimeStamp = window.sessionStorage.getItem('redirectIdxTimeStamp');

  const redirect_brand = getRegisterBrand();
  const eventData: AmplitudeEventData = {
    redirect_index: redirectIdx,
    redirect_brand
  };

  if (
    status === UnifiedRedirectActions.urlOnCancel ||
    status === UnifiedRedirectActions.urlOnExisting
  ) {
    const action = status === UnifiedRedirectActions.urlOnCancel ? 'Cancel' : 'Sign In';
    logExitFlow({ ...eventData, action });
  }

  if (redirectIdx && redirectIdx !== '000') {
    // We are using this webpage for multiple systems. It is possible, however unlikely someone could get the redirectIdx index set for multiple systems. An expiration seems like a safe fix.
    const oneHour = 1000 * 60 * 60;
    const expireTime = redirectIdxTimeStamp ? parseInt(redirectIdxTimeStamp, 10) + oneHour : 0;
    if (Date.now() <= expireTime) {
      try {
        // Get url from identity
        const res = await dispatch(getRedirectUrl(status, redirectIdx));
        const url = res.payload?.data?.data?.url || res.payload?.data?.url;
        if (url) {
          // Clear the redirectIdx and brand
          window.sessionStorage.setItem('redirectIdxTimeStamp', '');
          window.sessionStorage.setItem('redirectIdx', '');
          window.sessionStorage.setItem('registerBrand', '');
          window.location.href = url;
          return;
        }
        // if no url handle default action
      } catch (err) {
        Logger.warn('getRedirectUrl failed to find a path: ', err.message);
      }
    }
  }

  switch (status) {
    // urlOnComplete and urlOnExisting have the same navigation path
    case UnifiedRedirectActions.urlOnForgetUserName:
      dispatch(forgotUsername());
      break;
    case UnifiedRedirectActions.urlOnComplete:
    case UnifiedRedirectActions.urlOnExisting:
      // navigation happens in handleLoginPress
      dispatch(handleLoginPress('register'));
      break;
    // urlOnError and urlOnCancel have the same navigation path
    case UnifiedRedirectActions.urlOnError:
    case UnifiedRedirectActions.urlOnCancel:
    default:
      history.push('/');
  }
}

/**
 * Method: handleUrlOnError
 * Description:
 */
export const handleUrlOnError = (history: History) => (dispatch: RootDispatch) => {
  handleReturnToInitiator(UnifiedRedirectActions.urlOnError, history, dispatch);
};

/**
 * Method: handleUrlOnCancel
 * Description:
 */
export const handleUrlOnCancel = (history: History) => (dispatch: RootDispatch) => {
  dispatch(resetRegistration());
  handleReturnToInitiator(UnifiedRedirectActions.urlOnCancel, history, dispatch);
};

/**
 * Method: handleUrlOnExisting
 * Description:
 */
export const handleUrlOnExisting = (history: History) => (dispatch: RootDispatch) => {
  handleReturnToInitiator(UnifiedRedirectActions.urlOnExisting, history, dispatch);
};

/**
 * Method: handleUrlOnComplete
 * Description:
 */
export const handleUrlOnComplete = (history: History) => (dispatch: RootDispatch) => {
  handleReturnToInitiator(UnifiedRedirectActions.urlOnComplete, history, dispatch);
};

/**
 * Method: urlOnForgetUserName
 * Description:
 */
export const handleUrlOnForgetUserName = (history: History) => (dispatch: RootDispatch) => {
  handleReturnToInitiator(UnifiedRedirectActions.urlOnForgetUserName, history, dispatch);
};
